import React, { useRef, useState } from "react";
import { ProgressBar } from "react-bootstrap";
import PhotoGalleryService from "../../../_services/PhotoGalleryService";

interface Props {
  albumId: number;
  onUploadComplete: () => void;
}

const PhotoUploadZone: React.FC<Props> = ({ albumId, onUploadComplete }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    setProgress(0);

    // Chunk into batches of 20
    const batchSize = 20;
    let uploaded = 0;
    const total = files.length;
    setTotalCount(total);
    setUploadedCount(0);

    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      try {
        await PhotoGalleryService.uploadPhotos(batch, albumId, (e: any) => {
          if (e.total) {
            const batchProgress = Math.round((e.loaded * 100) / e.total);
            const overallProgress = Math.round(
              ((uploaded + (batchProgress / 100) * batch.length) / total) * 100,
            );
            setProgress(overallProgress);
          }
        });
        uploaded += batch.length;
        setUploadedCount(uploaded);
      } catch (err) {
        console.error("Upload batch failed", err);
      }
    }

    setUploading(false);
    setProgress(100);
    onUploadComplete();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/"),
    );
    uploadFiles(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  return (
    <div>
      <div
        className={`upload-zone ${dragOver ? "drag-over" : ""}`}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
        />
        <div style={{ fontSize: 40, color: "var(--text-faint)", marginBottom: 8 }}>
          <i className="fa fa-cloud-upload" />
        </div>
        <div>
          <strong>Drop photos here</strong> or click to browse
        </div>
        <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
          Supports JPG, PNG, WEBP. Up to 15MB each.
        </div>
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="upload-progress-item">
            <span>
              Uploading {uploadedCount}/{totalCount}...
            </span>
            <ProgressBar now={progress} style={{ flex: 1, height: 8 }} />
            <span>{progress}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUploadZone;
